/**
 * The three primitives every non-table view kind stores its column choices in
 * (`specs/M4-V2-kanban.md` §4, measured over all 18 real Kanban / Calendar / ItemsGallery
 * rows in `discovery/raw/snapshot-2026-09-10`).
 *
 * monday has exactly two shapes and both look like a map of column id to boolean:
 *
 *   - a **column-toggle map** — `{"all": true}`, or `{"person": false, "date": true, …}`.
 *     A column is shown when `byColumn[id] ?? all`, so `{"all":true}` means "every column"
 *     and `{"all":false,"date40":true}` means "only date40". Used by kanban's `card_columns`
 *     and `gallery_column`, calendar's `columns`, and the gallery's `card_columns`.
 *   - a **single-column map** — `{"<columnId>": true}`, or `{}` for "none". Used by kanban's
 *     `pivot_by_column` and `assignee_column`, calendar's `color_by_id`, and the gallery's
 *     `cover_column`.
 *
 * Both are read and written here so no view kind reimplements them, and both are total: a
 * malformed value degrades to the empty selection rather than throwing, because these come
 * out of a nine-year-old production account.
 */
import { z } from 'zod';
export const columnSelectionSchema = z.object({
    all: z.boolean().nullable(),
    byColumn: z.record(z.string(), z.boolean()),
});
export const EMPTY_COLUMN_SELECTION = { all: null, byColumn: {} };
export const ALL_COLUMNS_SELECTION = { all: true, byColumn: {} };
/** Is this column selected? */
export function isColumnSelected(selection, columnId) {
    return selection.byColumn[columnId] ?? selection.all ?? false;
}
/**
 * The selected column ids, **in the board's own column order** — never in the map's key
 * order, which is insertion order in a 2016 database row.
 */
export function selectedColumnIds(selection, boardColumnIds) {
    return boardColumnIds.filter((id) => isColumnSelected(selection, id));
}
/** `true` when nothing is selected and nothing is defaulted on — "the view was never configured". */
export function isEmptySelection(selection) {
    return selection.all !== true && Object.values(selection.byColumn).every((on) => !on);
}
export function columnSelectionFromMonday(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
        return EMPTY_COLUMN_SELECTION;
    }
    let all = null;
    const byColumn = {};
    for (const [key, value] of Object.entries(raw)) {
        if (typeof value !== 'boolean')
            continue;
        if (key === 'all')
            all = value;
        else
            byColumn[key] = value;
    }
    return { all, byColumn };
}
/** The inverse. The `all` key comes back only when monday had one (`all !== null`). */
export function columnSelectionToMonday(selection) {
    const out = {};
    if (selection.all !== null)
        out['all'] = selection.all;
    for (const [id, on] of Object.entries(selection.byColumn))
        out[id] = on;
    return out;
}
/**
 * The one column a single-column map names. `{"all":true}` is not a column id and yields
 * `null`; so does `{}`, `{"x":false}` and anything malformed.
 */
export function singleColumnFromMonday(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    for (const [key, value] of Object.entries(raw)) {
        if (key !== 'all' && value === true)
            return key;
    }
    return null;
}
export function singleColumnToMonday(columnId) {
    return columnId === null ? {} : { [columnId]: true };
}
//# sourceMappingURL=column-selection.js.map